import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';
import { ncbService } from '../services/ncbService';

const { FiArrowLeft, FiShoppingBag, FiHeart } = FiIcons;

const DEFAULT_LOCAL_ITEMS = [
  { item_title: "Pancit Habhab", item_description: "A beloved Lucban noodle dish traditionally served on a banana leaf and enjoyed without utensils.", image_url: "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvcG5n/46df0d60a82d8417a5ee68e826efefb4.png" },
  { item_title: "Lucban Longganisa", item_description: "Known for its bold garlicky flavor, Lucban Longganisa is one of the town’s most famous culinary treasures.", image_url: "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvcG5n/deef038ac8679dfc04d3d3d5f1a6ecfd.png" },
  { item_title: "Buntal Hats", item_description: "Beautifully woven from buri palm fibers, Buntal hats showcase Lucban’s proud tradition.", image_url: "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvcG5n/96789a7cdb34fce4cb9c395d4576c998.png" }
];

const LocalFinds = () => {
  const [content, setContent] = useState({ sections: {}, items: {} });

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchContent = async () => {
      try {
        const sections = await ncbService.getPageSections();
        const items = await ncbService.getPageSectionItems();
        const pageSections = sections.filter(s => s.page_slug === 'experience').reduce((acc, s) => ({ ...acc, [s.section_key]: s }), {});
        const pageItems = items.filter(i => i.page_slug === 'experience').reduce((acc, i) => {
          if (!acc[i.section_key]) acc[i.section_key] = [];
          acc[i.section_key].push(i);
          return acc;
        }, {});
        setContent({ sections: pageSections, items: pageItems });
      } catch (err) { console.warn("Backend content failed"); }
    };
    fetchContent();
  }, []);

  const getS = (key, fallback) => content.sections[key] || fallback;
  const getI = (key, fallback) => content.items[key] || fallback;

  const hero = getS('hero', { title: "Local Finds", subtitle: "Taste & Discover", image_url: "https://images.unsplash.com/photo-1533900298318-6b8da08a523e?auto=format&fit=crop&q=80" });
  const intro = getS('intro', { title: "Culinary Delights & Craftsmanship", body: "Lucban is a haven for food lovers and culture seekers. The town is famous nationwide for its culinary treasures." });
  const gridIntro = getS('grid_intro', { title: "Must-Try Local Finds in Lucban", subtitle: "“From flavorful hometown dishes to handmade keepsakes.”" });
  const localItems = getI('local_items', DEFAULT_LOCAL_ITEMS);

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Local Finds | R. Venue Apartelle Lucban",
    "description": "Savor the authentic flavors of Lucban. From garlicky Lucban Longganisa and Pancit Habhab to handcrafted buntal hats and native delicacies.",
    "url": "https://rvenueapartelle.lucban.ph/local-finds"
  };

  return (
    <div className="w-full">
      <SEO 
        title="Local Finds"
        description="Savor the authentic flavors of Lucban. From garlicky Lucban Longganisa and Pancit Habhab to handcrafted buntal hats and native delicacies."
        canonical="/local-finds"
        ogImage={hero.image_url}
        schema={pageSchema}
      />
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden pt-[100px]">
        <div className="absolute inset-0 z-0">
          <img src={hero.image_url} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-brand-olive/70" />
        </div>
        <div className="relative z-10 text-center px-6 mt-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-16 h-16 bg-brand-cream/10 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-sm border border-white/20">
            <SafeIcon icon={FiShoppingBag} className="text-brand-cream text-2xl" />
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-script text-3xl text-brand-cream mb-4">{hero.subtitle}</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-serif text-4xl md:text-6xl uppercase tracking-[0.2em] text-white">{hero.title}</motion.h1>
        </div>
      </section>

      <section className="py-24 bg-brand-cream">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 className="font-serif text-3xl uppercase tracking-widest text-brand-olive mb-8">{intro.title}</h2>
          <div className="w-20 h-[1px] bg-brand-olive/30 mx-auto mb-10" />
          <p className="text-brand-olive/80 text-lg leading-relaxed font-light text-left md:text-center whitespace-pre-wrap">{intro.body}</p>
          <div className="mt-16 flex flex-col md:flex-row items-center justify-center gap-6">
            <Link to="/rates" className="btn-primary">Plan Your Trip</Link>
            <Link to="/" className="inline-flex items-center gap-2 text-brand-olive font-bold uppercase tracking-widest text-xs hover:text-brand-dark transition-colors"><SafeIcon icon={FiArrowLeft} /> Back to Home</Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white border-y border-brand-olive/5">
        <div className="container mx-auto px-6 max-w-7xl text-center mb-16">
          <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-brand-olive mb-4">{gridIntro.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-20">
            {localItems.map((item, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="group flex flex-col bg-brand-cream/30 rounded-2xl overflow-hidden border border-brand-olive/5 shadow-sm hover:shadow-xl hover:bg-white transition-all duration-500">
                <div className="h-64 overflow-hidden relative"><img src={item.image_url} alt={item.item_title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" /></div>
                <div className="p-8 flex-grow text-left">
                  <h3 className="font-serif text-xl uppercase tracking-widest text-brand-olive mb-4">{item.item_title}</h3>
                  <p className="text-brand-olive/70 text-sm leading-relaxed font-light">{item.item_description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocalFinds;